import React, { useEffect, useState } from 'react';
import { Card, Collapse } from 'reactstrap';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CheckboxMarked from 'mdi-react/CheckboxMarkedIcon';
import CheckboxBlank from 'mdi-react/CheckboxBlankIcon';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import AppointmentTypeItem from './components/AppointmentTypeItem';
import { useGetAppointmentTypes } from './hooks/useGetAppointmentTypes';
import { useCreateAppointmentType } from './hooks/useCreateAppointmentType';
import { useUpdateAppointmentTypes } from './hooks/useUpdateAppointmentTypes';
import { useUpdateSingleAppointmentType } from './hooks/useUpdateSingleAppointmentType';
import { useDeleteAppointmentType } from './hooks/useDeleteAppointmentType';
// import { useDispatch } from 'react-redux';
// import { toggleLoading } from '../../../redux/actions/commonActions';
import ConfirmModal from './components/ConfirmModal';
import { toast } from 'react-toastify';

export default function AppointmentTypesList() {
  // const dispatch = useDispatch();
  const { data: initialTypes, refetch } = useGetAppointmentTypes();
  const { mutateAsync: createAppointmentType } = useCreateAppointmentType();
  const { mutateAsync: updateAppointmentTypes } = useUpdateAppointmentTypes();
  const { mutateAsync: deleteAppointmentType } = useDeleteAppointmentType();
  const { mutateAsync: updateSingleAppointmentType } =
    useUpdateSingleAppointmentType();

  const [isAppointmentTypesOpened, setIsAppointmentTypesOpened] =
    useState(false);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    onConfirm: null,
    message: '',
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    onConfirm: null,
    message: '',
  });

  useEffect(() => {
    if (initialTypes) {
      setAppointmentTypes(initialTypes.map((v) => v));
    }
  }, [initialTypes]);

  const handleAppointmentCollapse = () => {
    setIsAppointmentTypesOpened((prev) => !prev);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(appointmentTypes);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    setAppointmentTypes(reordered);
    try {
      await updateAppointmentTypes(
        reordered.map((item, i) => ({
          ...item,
          order: i,
        })),
      );
      refetch();
    } catch (e) {
      setAppointmentTypes(appointmentTypes);
    }
  };

  const toggleCheck = async (id) => {
    const item = appointmentTypes.find(({ _id }) => _id === id);

    if (item.isNew) {
      toast('Please, complete editing first', {
        type: 'info',
        style: { width: 'auto' },
      });
      return;
    }

    if (item.editable && !item.checked) {
      return setConfirmModal({
        isOpen: true,
        message:
          'Once this Appointment Type is checked, it will not be able to be edited and cannot be deleted. This ensures past appointments using it will always be able to be filtered by this name. Do you want to continue?',
        onConfirm: async () => {
          setConfirmModal({ ...confirmModal, isOpen: false });
          await updateSingleAppointmentType({
            ...item,
            checked: true,
          });
          refetch();
        },
      });
    }

    await updateSingleAppointmentType({
      ...item,
      checked: !item.checked,
    });
    refetch();
  };

  const handleAddCustomType = () => {
    const hasUnfinishedDraft = appointmentTypes.some(
      (t) => t.isNew || !t._id || !t.title?.trim(),
    );

    if (hasUnfinishedDraft) {
      toast(
        'You already have an unsaved Appointment Type. Please complete it before adding another.',
        { type: 'info', style: { width: 'auto' } },
      );
      return;
    }

    const tempId = `temp-${Date.now()}`;

    const draft = {
      _id: tempId,
      title: '',
      checked: false,
      editable: true,
      isCustom: true,
      order: appointmentTypes.length,
      isNew: true,
    };

    setAppointmentTypes((prev) => [...prev, draft]);
    setEditingId(tempId);
    setNewTypeName('');
  };

  const handleDelete = async (_id) => {
    const item = appointmentTypes.find((t) => t._id === _id);

    if (item?.isNew) {
      setAppointmentTypes((prev) => prev.filter((t) => t._id !== _id));
      setEditingId(null);
      setNewTypeName('');
      return;
    }

    setDeleteConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete the Appointment Type "${item.title}"?`,
      onConfirm: async () => {
        setDeleteConfirmModal((prev) => ({ ...prev, isOpen: false }));
        await deleteAppointmentType({ _id });
        refetch();
      },
    });
  };

  const handleEdit = (id) => {
    const item = appointmentTypes.find((t) => t._id === id);
    setNewTypeName(item.title || '');
    setEditingId(id);
  };

  const handleEditChange = (e) => {
    setNewTypeName(e.target.value);
  };

  const handleEditSave = async (id) => {
    const item = appointmentTypes.find(({ _id }) => _id === id);
    const updatedTitle = newTypeName.trim();

    if (!updatedTitle) {
      return;
    }

    if (item.isNew) {
      const newItem = await createAppointmentType({
        title: updatedTitle,
        checked: false,
        isCustom: true,
      });

      setAppointmentTypes((prev) =>
        prev.map((t) => (t._id === id ? newItem : t)),
      );
    } else {
      await updateSingleAppointmentType({
        ...item,
        title: updatedTitle,
      });
    }

    setEditingId(null);
    setNewTypeName('');
    refetch();
  };

  // const checkAll = async () => {
  //   const hasEditableUnchecked = appointmentTypes.some(
  //     (item) => item.editable && !item.checked,
  //   );

  //   if (hasEditableUnchecked) {
  //     return setConfirmModal({
  //       isOpen: true,
  //       message:
  //         'Some appointment types are still editable. Once you check all, they will become locked (non-editable). Proceed?',
  //       onConfirm: async () => {
  //         setConfirmModal({ ...confirmModal, isOpen: false });
  //         dispatch(toggleLoading(true));
  //         await updateAppointmentTypes(
  //           appointmentTypes.map((item) => ({
  //             ...item,
  //             checked: true,
  //           })),
  //         );
  //         refetch();
  //         dispatch(toggleLoading(false));
  //       },
  //     });
  //   }

  //   dispatch(toggleLoading(true));
  //   await updateAppointmentTypes(
  //     appointmentTypes.map((item) => ({
  //       ...item,
  //       checked: true,
  //     })),
  //   );
  //   refetch();
  //   dispatch(toggleLoading(false));
  // };

  // const uncheckAll = async () => {
  //   dispatch(toggleLoading(true));
  //   await updateAppointmentTypes(
  //     appointmentTypes.map((item) => ({
  //       ...item,
  //       checked: false,
  //     })),
  //   );

  //   refetch();
  //   dispatch(toggleLoading(false));
  // };

  return (
    <>
      <div>
        <div
          className="appointment-types-header"
          onClick={handleAppointmentCollapse}
        >
          <div className="checkbox-icon">
            {isAppointmentTypesOpened ? (
              <CheckboxMarked color="#E3E6EB" />
            ) : (
              <CheckboxBlank color="#E3E6EB" />
            )}
          </div>
          <div className="appointment-types-title">Appointment Types</div>
          <button
            className={`customize-arrow ${
              isAppointmentTypesOpened ? 'rotate-up' : ''
            }`}
          >
            <ChevronRightIcon />
          </button>
        </div>
        <div className="customize-item">
          <div className="customize-content flex-grow-1">
            <div className="customize-description mb-3">
              Appointment Types that are checked will appear as dropdown choices
              when staff create an Appointment in the web app or a client
              requests one in the mobile app.
            </div>
            <Collapse isOpen={isAppointmentTypesOpened}>
              {/* <div className="d-flex gap-2 mb-2">
                <button
                  onClick={uncheckAll}
                  className="btn loyal-btn-new-parent"
                >
                  Uncheck All
                </button>
                <button onClick={checkAll} className="btn loyal-btn-new-parent">
                  Check All
                </button>
              </div> */}
              <p className="drag-item-description">
                Drag items to change the order in which the choices for
                Appointment Types appear in dropdown menus.
              </p>
              <Card className="d-flex border rounded p-2 mb-2 mt-2">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="types">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {appointmentTypes.map((type, index) => (
                          <Draggable
                            key={type._id}
                            draggableId={type._id}
                            index={index}
                          >
                            {(provided) => (
                              <AppointmentTypeItem
                                type={type}
                                index={index}
                                provided={provided}
                                toggleCheck={toggleCheck}
                                editingId={editingId}
                                newTypeName={newTypeName}
                                handleEditChange={handleEditChange}
                                handleEdit={handleEdit}
                                handleEditSave={handleEditSave}
                                handleDelete={handleDelete}
                                setNewTypeName={setNewTypeName}
                              />
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Card>
              <button
                className="btn loyal-btn-new-parent mt-2"
                onClick={handleAddCustomType}
              >
                + Add Custom Type
              </button>
            </Collapse>
          </div>
        </div>
        <ConfirmModal
          centered
          isOpen={confirmModal.isOpen}
          toggle={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          message={confirmModal.message}
        />
        <ConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          toggle={() =>
            setDeleteConfirmModal({ ...deleteConfirmModal, isOpen: false })
          }
          onConfirm={deleteConfirmModal.onConfirm}
          message={deleteConfirmModal.message}
          title="Delete Appointment Type"
        />
      </div>
    </>
  );
}
